import { Component, computed, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { CommonModule } from '@angular/common';

import { ActionService } from '../../../../services/action.service';
import { DropdownObjectType } from '../../../../shared/types';
import { TaxonomyService } from '../../../../services/taxonomy.service';
import { CustomSelectComponent } from '../../../../components/custom-select/custom-select.component';
import { ConfirmPopupService } from '../../../../services/confirm-popup.service';
import { NotificationService } from '../../../../services/notification.service';
import { PopupConfirmationData } from '../../../../shared/constant';
import { IntentFormComponent } from './intent-form/intent-form.component';
import { RightPanelHeaderComponent } from '../../../../components/right-panel-header/right-panel-header.component';
import { CommonService } from '../../../../services/common.service';
import { delayLoading } from '../../../../shared/appUtil';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { LinkedItem, TaxonomyEntityFormGroup, TaxonomyRequest } from '../../taxonomy.model';
import { TaxonomyTypes } from '../../taxonomy.constants';
import { labelUniqueValidator } from '../../../../validators/label-duplicate.validator';

@Component({
  selector: 'app-entity-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, CustomSelectComponent, IntentFormComponent, RightPanelHeaderComponent],
  templateUrl: './entity-form.component.html',
  styleUrl: './entity-form.component.scss',
  animations: [
    trigger('contentAnimation', [
      state(
        'void',
        style({
          maxHeight: '0',
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      state(
        '*',
        style({
          maxHeight: '600px',
          opacity: 1,
        })
      ),
      transition('void <=> *', animate('300ms ease-in-out')),
    ]),
  ],
})
export class EntityFormComponent {
  constructor(
    private fb: FormBuilder,
    public actionsService: ActionService,
    public taxonomyService: TaxonomyService,
    private taxonomyApiService: TaxonomyApiService,
    private confirmPopupService: ConfirmPopupService,
    public notificationService: NotificationService,
    private commonService: CommonService
  ) {
    this.entityForm = this.fb.group({
      type: [TaxonomyTypes[0].value, Validators.required],
      label: ['', [Validators.required, this.noWhitespaceValidator]],
      description: ['', [Validators.required, this.noWhitespaceValidator]],
    });
  }

  _newIntentForm = signal<boolean>(false);
  _intentUpdated = signal<boolean>(false);
  _editIntentId = signal<number>(-1);
  _deleteIntentId = signal<number>(-1);
  _selectedTaxonomyItem = computed<any>(() => this.taxonomyService.taxonomyItem());
  _typeContent = TaxonomyTypes;
  originalFormValues: TaxonomyEntityFormGroup | undefined;
  applyButtonEnabled: boolean = false;
  entityForm: FormGroup;
  isSectionOneOpen = true;
  isSectionTwoOpen = true;
  isApplying: boolean = false;

  _selectedValue = this.taxonomyService.getSelectedValue();
  taxoSelectedValue = computed(() => this._selectedValue()?.taxonomy_name || 'Select Taxonomy');

  ngOnInit() {
    // Check lock status on initialization
    this.taxonomyService.checkLockStatus();

    // Conditionally apply the 'labelUniqueValidator' based on the form mode (create or update)
    if (this.taxonomyService.entitityFormFlag === 'create') {
      this.entityForm
        .get('label')
        ?.setValidators([
          Validators.required,
          this.noWhitespaceValidator,
          labelUniqueValidator(this.taxonomyService.selectedTaxonomyEntitiesList),
        ]);
    }

    // If you need to set default values in create mode
    if (this.taxonomyService.entitityFormFlag === 'create') {
      this.entityForm.patchValue({
        label: '',
        type: 'Primary Topic',
        description: '',
      });
    }

    // In case of 'update' form flag, you can fetch the entity details
    if (this.taxonomyService.entitityFormFlag === 'update') {
      this.getEnitityById();
    }

    // Watch for value changes
    this.entityForm.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });
  }

  // Custom validator to check for whitespace
  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  getTrimmedFormValues(): any {
    const raw = this.entityForm.value;
    return {
      ...raw,
      label: raw.label?.trim(),
      description: raw.description?.trim(),
    };
  }

  getEnitityById() {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');

    const body: any = {
      ...this.taxonomyService.enitity,
      record_id: local_taxonomy_version,
    };

    this.taxonomyApiService.getEnitityByIdV2(body).subscribe((res: any) => {
      this.taxonomyService.selectedEnitity = res.data;
      this.entityForm.patchValue({
        label: this.taxonomyService.selectedEnitity.EntityName,
        type: this.taxonomyService.selectedEnitity.EntityType,
        description: this.taxonomyService.selectedEnitity.EntityDescription,
      });
      this.originalFormValues = this.getTrimmedFormValues();
      this.checkFormChanges();
    });
  }

  checkFormChanges(): void {
    const currentValues = this.getTrimmedFormValues();
    const isValidLabel = currentValues.label?.length > 0;
    const isValidDescription = currentValues.description?.length > 0;

    const isFormDifferent = JSON.stringify(currentValues) !== JSON.stringify(this.originalFormValues);
    this.applyButtonEnabled = (isFormDifferent || this._intentUpdated()) && isValidLabel && isValidDescription;
  }

  toggleSectionTwo(event: MouseEvent) {
    this.isSectionTwoOpen = !this.isSectionTwoOpen;
    event.stopPropagation();
  }

  toggleSectionOne(event: MouseEvent) {
    this.isSectionOneOpen = !this.isSectionOneOpen;
    event.stopPropagation();
  }

  convertToTaxonomyRequest(input: any): TaxonomyRequest {
    const taxonomyId = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}').taxonomy_id || '';
    return {
      taxonomy_id: taxonomyId,
      entity_name: input.label.trim(),
      entity_type: input.type,
      entity_description: input.description.trim(),
      linked_items_type: input.linked_items_type || 'Intents',
      linked_entities:
        input.linked_items?.map((item: any) => ({
          label: item.label,
          description: item.description,
        })) ||
        input.LinkedEntities?.map((item: any) => ({
          label: item.label,
          description: item.description,
        })),
    };
  }

  async onApply() {
    // Prevent multiple rapid clicks
    if (this.isApplying) {
      return;
    }

    this.isApplying = true;

    try {
      const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
      const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');

      const trimmedValues = this.getTrimmedFormValues();
      this.entityForm.patchValue(trimmedValues, { emitEvent: false });

      let tempItem = {
        ...this.taxonomyService.selectedEnitity,
        ...trimmedValues,
      };

      const updatedTax: any = this.convertToTaxonomyRequest(tempItem);

      // Add record_id to the payload
      updatedTax.record_id = local_taxonomy_version;

      if (this.taxonomyService.entitityFormFlag == 'create') {
        this.taxonomyApiService.addEntityV2(updatedTax).subscribe(() => {
          this.taxonomyService.versionHistoryById();
        });
      } else if (this.taxonomyService.entitityFormFlag == 'update') {
        const updateTaxonomyBody: any = updatedTax;
        updateTaxonomyBody.entity_id = this.taxonomyService.enitity?.entity_id;

        this.taxonomyApiService.updateEntityV2(updateTaxonomyBody).subscribe((res) => {
          this.taxonomyService.versionHistoryById();
          this.notificationService.success('Update entity', 'Entity updated');
        });
      }

      await delayLoading(1);

      this.originalFormValues = this.getTrimmedFormValues();
      this.checkFormChanges();

      this.taxonomyService.triggerTaxonomyUpdate();
      this.actionsService.closeAddEntityForm();
    } catch (error) {
      console.error('Error applying entity:', error);
    } finally {
      this.isApplying = false;
    }
  }

  onClose() {
    if (this.entityForm.valid && (this.applyButtonEnabled || this._intentUpdated())) {
      this.confirmPopupService
        .openConfirmDialog(PopupConfirmationData.cancelTaxonomyConfirmation)
        .subscribe((result) => {
          if (result?.data?.confirm) {
            this.actionsService.closeAddEntityForm();
          }
        });
    } else {
      this.actionsService.closeAddEntityForm();
    }
  }

  onDelete() {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');

    // Try to get entity_id from multiple possible locations
    const entityId = this.taxonomyService.enitity?.entity_id ||
                    this.taxonomyService.selectedEnitity?.id ||
                    this.taxonomyService.selectedEnitity?.entity_id;

    // Get taxonomy_id from localStorage
    const taxonomyId = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}').taxonomy_id || '';

    // Create the correct body for delete API - it expects { taxonomy_id: string, entity_id: string }
    const body: any = {
      taxonomy_id: taxonomyId,
      entity_id: entityId,
      record_id: local_taxonomy_version,
    };

    // Check if both taxonomy_id and entity_id are available
    if (!taxonomyId) {
      // this.notificationService.error('Delete item', 'Taxonomy ID not found');
      return;
    }

    if (!entityId) {
      // this.notificationService.error('Delete item', 'Entity ID not found');
      return;
    }

    this.confirmPopupService.openConfirmDialog(PopupConfirmationData.deleteTaxonomyConfirmation).subscribe((result) => {
      if (result === true) {
        this.taxonomyApiService.deleteEntityV2(body).subscribe({
          next: async (res) => {
            await delayLoading(1);
            this.taxonomyService.versionHistoryById();

            this.taxonomyService.triggerTaxonomyUpdate();
            this.notificationService.success('Delete item', 'Entity deleted');
            this.actionsService.closeAddEntityForm();
          },
          error: (error) => {
            this.notificationService.error('Delete item', 'Failed to delete entity');
          }
        });
      }
    });
  }

  handleAddNewIntent() {
    this._newIntentForm.set(true);
  }

  handleCancelIntent() {
    this._newIntentForm.set(false);
  }

  handleIntentFormComplete() {
    this._intentUpdated.set(true);
    this._editIntentId.set(-1);
    this._newIntentForm.set(false);
    this.checkFormChanges();
  }

  handleCancelEditIntent() {
    this._editIntentId.set(-1);
  }

  handleEditIntent(index: number) {
    this._editIntentId.set(index);
    this._deleteIntentId.set(-1);
  }

  handleDeleteIntent(index: number) {
    this._deleteIntentId.set(index);
  }

  handleDeleteIntentCancel() {
    this._deleteIntentId.set(-1);
  }

  handleDeleteIntentConfirm(item: LinkedItem) {
    if (this.taxonomyService.selectedEnitity.LinkedEntities) {
      this.taxonomyService.selectedEnitity.LinkedEntities?.splice(this._deleteIntentId(), 1);
      this._intentUpdated.set(true);
      this.checkFormChanges();
    }
    this.handleDeleteIntentCancel();
  }

  get type(): DropdownObjectType {
    return {
      value: this.entityForm.get('type')?.value,
      label: this.entityForm.get('type')?.value,
    } as DropdownObjectType;
  }

  handleOperatorSelect(value: DropdownObjectType) {
    this.entityForm.patchValue({
      type: value.value,
    });
  }

  ngOnDestroy() {
    this.taxonomyService.selectedEnitity = { LinkedEntities: [] };
  }

  handleAddIntent() {
    if (!(this.taxonomyService.selectedEnitity.LinkedEntities == null)) {
      this.taxonomyService.selectedEnitity.LinkedEntities.push({
        label: '',
        description: '',
      });
      this.checkFormChanges();
      return;
    }
    let arr: any = [];
    this.taxonomyService.selectedEnitity.LinkedEntities = arr;
    this.checkFormChanges();
  }

  get isLinkedEntitiesEmpty(): boolean {
    return this.taxonomyService.selectedEnitity.LinkedEntities.length < 1;
  }

  get isSubmitDisabled(): boolean {
    // If locked, always disable submit
    if (this.taxonomyService.lockStatus) {
      return true;
    }

    // If currently applying, disable submit to prevent multiple clicks
    if (this.isApplying) {
      return true;
    }

    if (this.taxonomyService.entitityFormFlag == 'update') {
      return !this.applyButtonEnabled;
    } else {
      return !(this.entityForm.valid && this.taxonomyService.selectedEnitity.LinkedEntities.length > 0);
    }
  }
}
